/**
 * „Wybierz plan" — the block that turns the Subskrypcja tab from a readout into a checkout.
 *
 * Canon: `docs/design/subskrypcja-2026-09-21/`, variant A in Flame Dark, chosen by the owner on
 * 21 September 2026. Three cards under the shipped seats block, one shared features row, the fine
 * print, and nothing else — no payment history and no price written into this file.
 *
 * Every decision is `model/webOffers`' and `lib/useWebCheckout`'s: which plans are offered, which
 * one is current, which one is recommended, what a price says, and what happens when a button is
 * pressed. This file lays them out.
 *
 * ── The period switch is inside the card that has a choice ──────────────────────────────────
 * Only one level is sold both monthly and yearly, so the switch belongs to that card rather than to
 * the block. `SegmentedChoice` is the app's one segmented control, in its `ink` cut — a card's own
 * ground needs the filled option, and the radio wiring, the group name and the ring are all the
 * same control the Aplikacja tab draws.
 */

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { PlanMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { FormBanner } from '~/shared/ui/FormBanner';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { WebCheckoutApi } from '../lib/useWebCheckout';
import type { PlanOffer } from '../model/webOffers';
import { ExternalIcon } from './AccountIcons';
import { SegmentedChoice } from '~/shared/ui/SegmentedChoice';
import screen from './AccountScreen.module.css';
import styles from './PlanPicker.module.css';
import { PromoLine, TrialCodeRow } from './TrialCodeRow';

interface PlanPickerProps {
  readonly checkout: WebCheckoutApi;
}

/** The six things every plan carries. One row under the cards, never three repeated lists. */
const EVERY = [
  'everyBuilder',
  'everyProgression',
  'everyExercises',
  'everyVideo',
  'everyChat',
  'everyBrand',
] as const;

/**
 * The card's own wait, in the shape of the card — see the stylesheet.
 *
 * The BOX is drawn whether or not the bars are: it is the reserve, and it stands from the first
 * frame. The bars are the skeleton gate's — `useSkeletonGate`'s 150ms grace, so a catalogue that
 * answers in 5ms never flashes a shimmer at anybody — and this component draws what the gate says
 * rather than what the query says. An earlier pass branched on `state === 'pending'` here, which
 * made the gate's answer dead code and put a shimmer on every local load.
 */
function CardSkeleton({ bars }: { readonly bars: boolean }) {
  return (
    <div className={styles.card}>
      {bars && (
        <>
          <span className={`${String(skel.bar)} ${String(styles.skeletonName)}`} />
          <span className={`${String(skel.bar)} ${String(styles.skeletonSub)}`} />
          <span className={`${String(skel.bar)} ${String(styles.skeletonAmount)}`} />
          <span className={`${String(skel.bar)} ${String(styles.skeletonSeats)}`} />
          <span className={`${String(skel.bar)} ${String(styles.skeletonButton)}`} />
        </>
      )}
    </div>
  );
}

/** What a card's button says — the level it leads to, worded for the place it leads to. */
function ctaLabel(offer: PlanOffer, checkout: WebCheckoutApi, busy: boolean, t: TFunction): string {
  if (offer.isCurrent) return t('web:account.subscription.picker.currentCta');
  if (checkout.route === 'portal') {
    return busy
      ? t('web:account.subscription.picker.openingPortal')
      : t('web:account.subscription.picker.changeTo', { plan: offer.name });
  }
  if (busy) return t('web:account.subscription.picker.opening');
  // A code applied on the year makes this the trial's button; everywhere else it names the level.
  return checkout.trialCopyOf(offer)?.cta ?? t('web:account.subscription.picker.choose', { plan: offer.name });
}

function PlanCard({ offer, checkout }: { readonly offer: PlanOffer; readonly checkout: WebCheckoutApi }) {
  const { t } = useTranslation();
  const face = checkout.faceOf(offer);
  const trialFace = checkout.trialFaceOf(offer);
  // On the portal route every card leads to the same place, so the portal's wait is every card's:
  // the one pressed says so, the others refuse beside it. A web subscriber changes level in the
  // portal (R4), and the checkout would only answer `webLive`.
  const busy =
    checkout.route === 'portal' ? checkout.portalBusy && !offer.isCurrent : checkout.buying === offer.code;
  // Every other button refuses while one is leaving for the payment page: the document is about to
  // be replaced, and a second press would mint a second session nobody completes.
  const elsewhereBusy = checkout.route !== 'portal' && checkout.buying !== null && !busy;
  const refused = offer.isCurrent || elsewhereBusy;
  const buttonClass = `${
    offer.isRecommended && !offer.isCurrent
      ? String(ink.ink)
      : `${String(ghost.ghost)} ${String(styles.buttonGhost)}`
  } ${String(styles.button)}`;

  return (
    <div
      className={`${String(styles.card)} ${offer.isCurrent ? String(styles.current) : ''} ${
        offer.isRecommended ? String(styles.lead) : ''
      }`}
    >
      <p className={styles.name}>
        {offer.name}
        {offer.isCurrent && <span className={styles.flag}>{t('web:account.subscription.picker.currentFlag')}</span>}
        {!offer.isCurrent && offer.isRecommended && (
          <span className={`${String(styles.flag)} ${String(styles.flagLead)}`}>
            {t('web:account.subscription.picker.recommendedFlag')}
          </span>
        )}
      </p>

      <p className={styles.sub}>{offer.sub}</p>

      {face !== null && (
        <p className={styles.price}>
          <b className={styles.amount}>{face.price.amount}</b>
          <span className={styles.unit}>
            <b className={styles.unitLead}>{face.price.unit}</b>
            {face.price.note}
          </span>
        </p>
      )}

      {offer.periods.length > 1 && (
        <div className={styles.switch}>
          <SegmentedPeriods offer={offer} checkout={checkout} />
        </div>
      )}

      {/* The card that carries the trial code draws its fact line inside the row's own rows. An
        * account that already subscribes is offered no code, and its card is the plain one. */}
      {trialFace !== null && trialFace.kind !== 'refusedByAccount' ? (
        <TrialCodeRow
          face={trialFace}
          copy={checkout.trialCopyOf(offer)}
          trial={checkout.trial}
          promoted={face?.promoted === true}
          busy={busy}
          onToAnnual={() => {
            checkout.onTrialToAnnual(offer);
          }}
        />
      ) : (
        face?.promoted === true && <PromoLine />
      )}

      <p className={styles.seats}>
        <b className={styles.seatsCount}>{offer.seats}</b>
        <span className={styles.seatsSelf}>{t('web:account.subscription.picker.seatsSelf')}</span>
      </p>

      <div className={styles.cta}>
        {/* A store subscription changes in the store, and the store's settings page is an address
          * that does not change per press — so a real link, like the „Zarządzaj" row under it. */}
        {checkout.route === 'store' && checkout.store !== null && !offer.isCurrent ? (
          <a
            className={buttonClass}
            href={checkout.store.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('web:account.app.help.opensExternally', {
              label: t('web:account.subscription.picker.inStore', { store: checkout.store.name }),
            })}
          >
            <ExternalIcon className={styles.ctaIcon} />
            {t('web:account.subscription.picker.inStore', { store: checkout.store.name })}
          </a>
        ) : (
        <button
          type="button"
          className={buttonClass}
          // `aria-disabled` rather than `disabled`: a control carrying a reason has to stay in the
          // tab order for the reason to be reachable at all (view ADR §12), and „Twój plan" IS the
          // reason this one is refused.
          aria-disabled={refused || busy}
          onClick={() => {
            if (refused || busy) return;
            if (checkout.route === 'portal') checkout.onOpenPortal();
            else checkout.onBuy(offer);
          }}
        >
          {ctaLabel(offer, checkout, busy, t)}
          {/* „Twój plan" IS this button's reason, and a button refused because ANOTHER one is
            * opening the payment page owes the same courtesy — otherwise it carries
            * `aria-disabled` and no account of itself. The reason is the SAME sentence the busy
            * button beside it wears in ink (`opening`), not a second wording of it; it stays off
            * the screen here because that button is already saying it. */}
          {elsewhereBusy && (
            <span className={hidden.srOnly}>
              {` — ${t('web:account.subscription.picker.opening')}`}
            </span>
          )}
        </button>
        )}
      </div>
    </div>
  );
}

/** The billing period, as the app's one segmented control in its card cut. */
function SegmentedPeriods({
  offer,
  checkout,
}: {
  readonly offer: PlanOffer;
  readonly checkout: WebCheckoutApi;
}) {
  const { t } = useTranslation();
  const selected = checkout.periodOf(offer);
  if (selected === null) return null;

  return (
    <SegmentedChoice
      legend={t('web:account.subscription.picker.periodLegend')}
      options={offer.periods.map((option) => ({
        value: option.period,
        label: option.label,
        ...(option.saving === null ? {} : { badge: option.saving }),
      }))}
      value={selected}
      tone="ink"
      onChange={(period) => {
        checkout.onSelectPeriod(offer.code, period);
      }}
    />
  );
}

export function PlanPicker({ checkout }: PlanPickerProps) {
  const { t } = useTranslation();

  return (
    /* `styles.block` beside the screen's own: it declares the containment the cards' own
     * breakpoint is measured against, and nothing else. See the stylesheet. */
    <div
      className={`${String(screen.block)} ${String(styles.block)}`}
      aria-busy={checkout.state === 'pending'}
    >
      {/* „Wybierz plan" for somebody on Free, „Zmień plan" for somebody already paying. Until the
        * entitlement answers nobody knows which, and the first is the likelier — the swap changes
        * ink only, in a line of the same height. */}
      <h3 className={screen.blockTitle}>
        {checkout.route === 'portal' || checkout.route === 'store'
          ? t('web:account.subscription.picker.titleChange')
          : t('web:account.subscription.picker.title')}
      </h3>

      {checkout.state === 'pending' && (
        <span className={hidden.srOnly}>{t('web:account.subscription.picker.waiting')}</span>
      )}

      {checkout.state === 'error' ? (
        <>
          <FormBanner message={t('web:account.subscription.picker.loadFailed')} />
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(screen.accountButton)} ${String(screen.canvasGhost)} ${String(styles.retry)}`}
            onClick={checkout.onRetry}
          >
            {t('common.retry')}
          </button>
        </>
      ) : (
        <>
          {/* The reveal goes on the GRID rather than on each card: the cards are what the answer
            * brings with it, and three separate fades over one arrival is three gestures where the
            * policy asks for one. The boxes underneath do not move — the skeleton stands in the
            * same grid, with the same floor. */}
          <div
            className={`${String(styles.cards)} ${
              /* The code row's floor wherever the Pro card can carry it — and while the entitlement
               * is still unanswered, because the free trainer is the likelier answer and the
               * taller shape is the one that must not push what stands below. */
              checkout.route === null || checkout.route === 'checkout' ? String(styles.withCode) : ''
            } ${checkout.state === 'pending' ? '' : String(checkout.reveal)}`}
          >
            {checkout.state === 'pending' ? (
              // Three, because three is what this catalogue answers with — the boxes are the
              // reserve and the bars stand inside them once the gate opens.
              [0, 1, 2].map((slot) => <CardSkeleton key={slot} bars={checkout.skeleton} />)
            ) : checkout.sellable ? (
              checkout.offers.map((offer) => (
                <PlanCard key={offer.code} offer={offer} checkout={checkout} />
              ))
            ) : (
              /* The read is IN and there is nothing on sale: every web row came back without a
               * price, which is what the provider being unreachable looks like from here. It is an
               * empty state rather than a Free card standing alone under „Wybierz plan", and it
               * stands in the cards' own box so the block does not change size to say it. */
              <div className={styles.nothing}>
                <EmptyState
                  mark={<PlanMarkIcon />}
                  tone="action"
                  /* One sentence and no second one. „Spróbuj za chwilę." stood directly above a
                   * „Ponów" button that says the same thing better, and it was the only hint on
                   * the block. */
                  title={t('web:account.subscription.picker.emptyTitle')}
                />
                <button
                  type="button"
                  /* `stateAction`, not the screen's own button box: this retry stands on a CARD,
                   * which is the split that stylesheet exists for. */
                  className={`${String(ghost.ghost)} ${String(stateAction.stateAction)} ${String(styles.nothingRetry)}`}
                  onClick={checkout.onRetry}
                >
                  {t('common.retry')}
                </button>
              </div>
            )}
          </div>

          {checkout.refusal !== null && (
            <div className={styles.refusal}>
              <FormBanner message={checkout.refusal} />
            </div>
          )}

          <p className={styles.every}>
            <b className={styles.everyTitle}>{t('web:account.subscription.picker.everyTitle')}</b>
            {EVERY.map((key) => (
              <span key={key} className={styles.everyItem}>
                {t(`web:account.subscription.picker.${key}`)}
              </span>
            ))}
          </p>

          {/* The fine print is about the place a press leads: the payment page's terms for a buyer,
            * and for a subscriber where the change happens and when it takes effect (R8). */}
          <p className={styles.fine}>
            {checkout.route === 'portal' ? (
              <>
                <b className={styles.fineLead}>{t('web:account.subscription.picker.portalLead')}</b>{' '}
                {t('web:account.subscription.picker.portalRest')}
              </>
            ) : checkout.route === 'store' && checkout.store !== null ? (
              <>
                <b className={styles.fineLead}>
                  {t('web:account.subscription.picker.storeLead', { store: checkout.store.name })}
                </b>{' '}
                {t('web:account.subscription.picker.storeRest', { store: checkout.store.name })}
              </>
            ) : (
              <>
                <b className={styles.fineLead}>{t('web:account.subscription.picker.fineLead')}</b>{' '}
                {/* A trial's checkout offers no promotion-code field (R19), so the ordinary line's
                  * „Masz kod rabatowy…" would send the trainer looking for one. It says when the
                  * card is charged instead. */}
                {checkout.offers.some((offer) => checkout.trialFaceOf(offer)?.kind === 'applied')
                  ? t('web:account.subscription.trial.fine')
                  : t('web:account.subscription.picker.fineRest')}
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}
